import { create } from 'zustand';
import type { Unsubscribe } from '@firebase/firestore';
import { PeopleList, Person } from '../types';
import { peopleListsApi, type PersonInput } from '../api/peopleLists';
import { useAuthStore } from './authStore';
import { createPrefixedLogger } from '../utils/logger';
import { MY_PEOPLE_LIST_NAME, areaIdsForScope, findMyPeopleList } from '../utils/peopleLists';

const log = createPrefixedLogger('peopleListsStore');

// Module-level handles, the same reason as areaContactsStore: a store's own
// state is no place to keep an Unsubscribe across StrictMode double-invokes.
let listsUnsub: Unsubscribe | null = null;
let listsKey: string | null = null;
// One listener per list's people, shared by every view that wants them.
const peopleSubs = new Map<string, { unsub: Unsubscribe; refs: number }>();
// "My people" lists made this session, by area. The lists snapshot holds a
// new list back until the server confirms it, so a second "Add a person" a
// moment after the first would not find it and would make a second list.
const myPeopleListIds = new Map<string, string | Promise<string>>();

/** What the "Add a person" form collects. Blank fields are left out. */
export interface NewPersonInput {
  name: string;
  organization?: string;
  title?: string;
  /** Free text such as "HOA"; see categoryToPartyType. */
  category?: string;
  email?: string;
  phone?: string;
}

interface PeopleListsState {
  lists: PeopleList[];
  /** False until the first snapshot (or error) arrives. */
  listsLoaded: boolean;
  peopleByList: Record<string, Person[]>;
  error: string | null;

  /** Own lists only (the rules allow nothing else). Safe to call twice. */
  subscribeLists: (uid: string) => void;
  /** Starts (or shares) the listener for one list's people. Call the
   *  returned function to let go; the listener ends with its last holder. */
  retainPeople: (listId: string) => () => void;

  createList: (
    name: string,
    areaIds: string[],
    people: PersonInput[],
    onProgress?: (written: number, total: number) => void
  ) => Promise<string>;
  /** Adds one person, on Team, to this area's "My people" list. The list is
   *  made on first use. Throws when the write fails. */
  addPerson: (areaId: string, input: NewPersonInput) => Promise<void>;
  setTeam: (listId: string, personId: string, team: boolean) => Promise<void>;
  renameList: (listId: string, name: string) => Promise<void>;
  deleteList: (listId: string) => Promise<void>;

  reset: () => void;
}

export const usePeopleListsStore = create<PeopleListsState>((set, get) => ({
  lists: [],
  listsLoaded: false,
  peopleByList: {},
  error: null,

  subscribeLists: (uid) => {
    if (listsUnsub && listsKey === uid) return;
    listsUnsub?.();
    listsKey = uid;
    listsUnsub = peopleListsApi.subscribeLists(
      uid,
      (lists) => set({ lists, listsLoaded: true, error: null }),
      (error) => {
        log.warn('subscribeLists failed:', error);
        set({ listsLoaded: true, error: error.message });
      }
    );
  },

  retainPeople: (listId) => {
    const existing = peopleSubs.get(listId);
    if (existing) {
      existing.refs += 1;
    } else {
      const unsub = peopleListsApi.subscribePeople(
        listId,
        (people) => set((s) => ({ peopleByList: { ...s.peopleByList, [listId]: people } })),
        (error) => log.warn('subscribePeople failed:', error)
      );
      peopleSubs.set(listId, { unsub, refs: 1 });
    }
    let released = false;
    return () => {
      if (released) return;
      released = true;
      const sub = peopleSubs.get(listId);
      if (!sub) return;
      sub.refs -= 1;
      if (sub.refs <= 0) {
        sub.unsub();
        peopleSubs.delete(listId);
      }
    };
  },

  createList: async (name, areaIds, people, onProgress) => {
    const user = useAuthStore.getState().user;
    if (!user) throw new Error('Sign in to upload a list.');
    const id = peopleListsApi.newListId();
    await peopleListsApi.createList(id, { ownerUid: user.id, name, areaIds, people }, onProgress);
    return id;
  },

  addPerson: async (areaId, input) => {
    const user = useAuthStore.getState().user;
    if (!user) throw new Error('Sign in to add a person.');
    const email = input.email?.trim();
    const person: PersonInput = {
      name: input.name.trim(),
      organization: input.organization?.trim() || undefined,
      title: input.title?.trim() || undefined,
      category: input.category?.trim() || undefined,
      emails: email ? [email] : [],
      phone: input.phone?.trim() || undefined,
      sourceRow: 0,
      team: true,
    };
    const known = findMyPeopleList(get().lists, areaId)?.id ?? myPeopleListIds.get(areaId);
    if (known) {
      await peopleListsApi.addPerson(await known, person);
      return;
    }
    const creating = get().createList(MY_PEOPLE_LIST_NAME, areaIdsForScope('here', areaId), [
      person,
    ]);
    myPeopleListIds.set(areaId, creating);
    try {
      myPeopleListIds.set(areaId, await creating);
    } catch (error) {
      myPeopleListIds.delete(areaId);
      throw error;
    }
  },

  setTeam: async (listId, personId, team) => {
    // Optimistic: the toggle moves the row now; the snapshot confirms it.
    const before = get().peopleByList[listId];
    if (before) {
      set((s) => ({
        peopleByList: {
          ...s.peopleByList,
          [listId]: before.map((p) => (p.id === personId ? { ...p, team } : p)),
        },
      }));
    }
    try {
      await peopleListsApi.setTeam(listId, personId, team);
    } catch (error) {
      if (before) set((s) => ({ peopleByList: { ...s.peopleByList, [listId]: before } }));
      throw error;
    }
  },

  renameList: async (listId, name) => {
    await peopleListsApi.renameList(listId, name);
  },

  deleteList: async (listId) => {
    await peopleListsApi.deleteList(listId);
    for (const [area, id] of myPeopleListIds) if (id === listId) myPeopleListIds.delete(area);
    set((s) => {
      const rest = { ...s.peopleByList };
      delete rest[listId];
      return { peopleByList: rest };
    });
  },

  reset: () => {
    listsUnsub?.();
    listsUnsub = null;
    listsKey = null;
    for (const { unsub } of peopleSubs.values()) unsub();
    peopleSubs.clear();
    myPeopleListIds.clear();
    set({ lists: [], listsLoaded: false, peopleByList: {}, error: null });
  },
}));
